import { createTheme, type Theme } from '@mui/material/styles';

/* ══════════════════════════════════════════════════════════════
 * Theme system — two themes: 'dark' (default) and 'legacy' (GWT blue)
 * ══════════════════════════════════════════════════════════════ */

export type ThemeMode = 'dark' | 'legacy';

const THEME_KEY = 'brit_theme';
let _activeMode: ThemeMode = (localStorage.getItem(THEME_KEY) as ThemeMode) || 'dark';

export function getActiveThemeMode(): ThemeMode {
  return _activeMode;
}

export function setActiveThemeMode(mode: ThemeMode) {
  _activeMode = mode;
  localStorage.setItem(THEME_KEY, mode);
}

/* ── Style tokens ── */
export type TabStyle = 'classic' | 'flat';

interface AppStyles {
  mainView: string;
  tabContent: string;
  tabTitle: string;
  gridRowDefault: string;
  gridRowOdd: string;
  gridRowEven: string;
  detailPanel: string;
  detailValue: string;
  popupBg: string;
  popupBorder: string;
  contextPopupBg: string;
  contextPopupBorder: string;
  tabGradientStart: string;
  tabGradientEnd: string;
  tabSelectedEnd: string;
  tabStyle: TabStyle;
  buttonMenuStart: string;
  buttonMenuEnd: string;
  wizardDetail: string;
  wizardActive: string;
  headerBg: string;
  headerText: string;
  accentPrimary: string;
  accentHover: string;
  borderSubtle: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
}

const darkStyles: AppStyles = {
  mainView: '#0f1117',
  tabContent: '#161822',
  tabTitle: '#8ab4f8',
  gridRowDefault: '#1a1d2e',
  gridRowOdd: '#1e2133',
  gridRowEven: '#1a1d2e',
  detailPanel: '#12141e',
  detailValue: '#1e2133',
  popupBg: '#1e2133',
  popupBorder: '#2d3348',
  contextPopupBg: '#1e2133',
  contextPopupBorder: '#2d3348',
  tabGradientStart: '#1e2133',
  tabGradientEnd: '#12141e',
  tabSelectedEnd: '#161822',
  tabStyle: 'flat' as TabStyle,
  buttonMenuStart: '#1e2133',
  buttonMenuEnd: '#252a3a',
  wizardDetail: '#161822',
  wizardActive: '#252a3a',
  headerBg: '#0c0e15',
  headerText: '#e1e3e8',
  accentPrimary: '#5b8def',
  accentHover: '#7aa5ff',
  borderSubtle: '#2d3348',
  textPrimary: '#e1e3e8',
  textSecondary: '#9ca3b4',
  textMuted: '#6b7280',
};

const legacyStyles: AppStyles = {
  mainView: '#e2d6c7',
  tabContent: '#F3F7FB',
  tabTitle: 'darkblue',
  gridRowDefault: '#D8DFE6',
  gridRowOdd: '#E0E5EB',
  gridRowEven: '#D8DFE6',
  detailPanel: '#e2d6c7',
  detailValue: '#F3F7FB',
  popupBg: '#FAF0E6',
  popupBorder: 'darkblue',
  contextPopupBg: '#F1F7FE',
  contextPopupBorder: '#718BB7',
  tabGradientStart: '#D0E4F6',
  tabGradientEnd: '#92A0AD',
  tabSelectedEnd: '#F3F7FB',
  tabStyle: 'classic' as TabStyle,
  buttonMenuStart: 'rgb(235,243,253)',
  buttonMenuEnd: 'rgb(217,232,251)',
  wizardDetail: 'AliceBlue',
  wizardActive: 'LavenderBlush',
  headerBg: '#e2d6c7',
  headerText: '#000000',
  accentPrimary: '#00008b',
  accentHover: '#0000cd',
  borderSubtle: '#718BB7',
  textPrimary: '#000000',
  textSecondary: '#333333',
  textMuted: '#666666',
};

/** Reactive style tokens — reads the current active theme */
export const gwtStyles: AppStyles = new Proxy(darkStyles, {
  get(_target, prop: string) {
    const styles = _activeMode === 'dark' ? darkStyles : legacyStyles;
    return styles[prop as keyof AppStyles];
  },
});

/* ── MUI Themes ── */

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#5b8def',
      light: '#7aa5ff',
      dark: '#3d6fd9',
    },
    secondary: {
      main: '#9ca3b4',
    },
    background: {
      default: '#0f1117',
      paper: '#161822',
    },
    text: {
      primary: '#e1e3e8',
      secondary: '#9ca3b4',
    },
    divider: '#2d3348',
    error: {
      main: '#ef5350',
    },
    warning: {
      main: '#ff9800',
    },
    success: {
      main: '#4caf50',
    },
    info: {
      main: '#5b8def',
    },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", "Helvetica", Arial, sans-serif',
    fontSize: 13,
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#0f1117',
          overflow: 'hidden',
          margin: 0,
          padding: 0,
        },
        '*': {
          boxSizing: 'border-box',
        },
        '::-webkit-scrollbar': {
          width: '6px',
          height: '6px',
        },
        '::-webkit-scrollbar-track': {
          background: '#0f1117',
        },
        '::-webkit-scrollbar-thumb': {
          background: '#2d3348',
          borderRadius: '3px',
        },
        '::-webkit-scrollbar-thumb:hover': {
          background: '#3d4558',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#0c0e15',
          color: '#e1e3e8',
          boxShadow: 'none',
          borderBottom: '1px solid #2d3348',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: '6px',
          fontSize: '0.8125rem',
        },
        contained: {
          backgroundColor: '#5b8def',
          '&:hover': {
            backgroundColor: '#7aa5ff',
          },
        },
        outlined: {
          borderColor: '#2d3348',
          color: '#e1e3e8',
          '&:hover': {
            borderColor: '#5b8def',
            backgroundColor: 'rgba(91, 141, 239, 0.08)',
          },
        },
        text: {
          color: '#9ca3b4',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: '#e1e3e8',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#9ca3b4',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            color: '#e1e3e8',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          minHeight: 32,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: '#2d3348',
            },
            '&:hover fieldset': {
              borderColor: '#5b8def',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#5b8def',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e2133',
          border: '1px solid #2d3348',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e2133',
          border: '1px solid #2d3348',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          '&:hover': {
            backgroundColor: 'rgba(91, 141, 239, 0.12)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#252a3a',
          border: '1px solid #2d3348',
          fontSize: '0.75rem',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e2133',
          border: '1px solid #2d3348',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#2d3348',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
        },
      },
    },
  },
});

/**
 * GWT Blue Theme — pixel-level match of the existing GWT application.
 * Kept as "legacy" option for comparison.
 */
export const gwtBlueTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#00008b',
      light: '#D0E4F6',
      dark: '#000060',
    },
    secondary: {
      main: '#92A0AD',
    },
    background: {
      default: '#e2d6c7',
      paper: '#F3F7FB',
    },
    text: {
      primary: '#000000',
      secondary: '#333333',
    },
    divider: '#718BB7',
  },
  typography: {
    fontFamily: 'Arial, sans-serif',
    fontSize: 13,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#e2d6c7',
          overflow: 'hidden',
          margin: 0,
          padding: 0,
        },
        '*': {
          boxSizing: 'border-box',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#e2d6c7',
          color: '#000000',
          boxShadow: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: 'Arial, sans-serif',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: 'Arial, sans-serif',
          minHeight: 32,
        },
      },
    },
  },
});

/** Get the MUI theme for the given mode */
export function getMuiTheme(mode: ThemeMode): Theme {
  return mode === 'dark' ? darkTheme : gwtBlueTheme;
}
